import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Mail, Phone, UserX, PlugZap, Loader2, ShieldAlert, UsersRound } from "lucide-react";

// Debounce helper — returns the value only after `delay` ms of no changes.
function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

function telHref(phone) {
  if (!phone) return null;
  const digits = phone.replace(/[^0-9+]/g, "");
  return digits ? `tel:${digits}` : null;
}

export default function People() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 300);
  const [state, setState] = useState({
    loading: false,
    connected: null, // null = unknown
    results: [],
    error: null,
  });
  const reqRef = useRef(0);

  useEffect(() => {
    // Always hit the backend once on mount so we can show "Not connected" even with an empty query.
    const myReq = ++reqRef.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    api
      .get(`/people/search?q=${encodeURIComponent(debouncedQ || "")}`)
      .then(({ data }) => {
        if (reqRef.current !== myReq) return; // stale
        setState({
          loading: false,
          connected: !!data.connected,
          results: data.results || [],
          error: data.error || null,
        });
      })
      .catch(() => {
        if (reqRef.current !== myReq) return;
        setState({ loading: false, connected: null, results: [], error: "Unable to reach search service." });
      });
  }, [debouncedQ]);

  const showResults = state.connected && debouncedQ.trim().length > 0;
  const hasResults = showResults && state.results.length > 0;
  const emptyResults = showResults && !state.loading && state.results.length === 0 && !state.error;

  const resultCount = useMemo(() => state.results.length, [state.results]);

  return (
    <div className="space-y-8" data-testid="people-page">
      {/* Header */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">People lookup</p>
        <h2 className="font-heading text-3xl font-semibold text-brand-primary mt-1">
          Find anyone in the church, fast.
        </h2>
        <p className="text-sm text-brand-primary/65 mt-2 max-w-2xl">
          Search the church community database by name, email, or phone. Results pull live from Pushpay ChMS
          (Church Community Builder) — no need to open the portal.
        </p>
      </div>

      {/* Search bar */}
      <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden">
        <div className="relative">
          <Search className="h-5 w-5 absolute left-5 top-1/2 -translate-y-1/2 text-brand-primary/45 pointer-events-none" />
          <Input
            autoFocus
            data-testid="people-search-input"
            type="search"
            placeholder={state.connected === false
              ? "Connect Pushpay ChMS to start searching…"
              : "Search by name, email, or phone…"}
            disabled={state.connected === false}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-16 pl-14 pr-16 border-0 rounded-2xl text-base focus-visible:ring-0 bg-transparent"
          />
          {state.loading && state.connected && (
            <Loader2 className="h-5 w-5 absolute right-5 top-1/2 -translate-y-1/2 text-terracotta animate-spin" data-testid="people-search-spinner" />
          )}
          {!state.loading && showResults && (
            <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xs uppercase tracking-wider text-brand-primary/50 font-semibold" data-testid="people-result-count">
              {resultCount} {resultCount === 1 ? "match" : "matches"}
            </span>
          )}
        </div>
      </Card>

      {/* States */}
      {state.connected === false && (
        <NotConnectedState isAdmin={user?.role === "admin"} />
      )}

      {state.connected && state.error && (
        <Card className="rounded-2xl bg-white border border-terracotta/20 shadow-warm p-6 flex items-start gap-4" data-testid="people-error-state">
          <div className="h-10 w-10 rounded-xl bg-terracotta/10 grid place-items-center shrink-0">
            <ShieldAlert className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-brand-primary">Search failed</p>
            <p className="text-sm text-brand-primary/70 mt-1 break-words">{state.error}</p>
          </div>
        </Card>
      )}

      {state.connected && !debouncedQ.trim() && !state.error && (
        <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-10 text-center" data-testid="people-empty-prompt">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-sage/20 grid place-items-center">
            <UsersRound className="h-6 w-6 text-brand-primary" strokeWidth={1.7} />
          </div>
          <p className="font-heading text-xl text-brand-primary mt-4">Start typing to search</p>
          <p className="text-sm text-brand-primary/60 mt-1">
            Try a first name, last name, email, or phone number.
          </p>
        </Card>
      )}

      {emptyResults && (
        <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-10 text-center" data-testid="people-no-results">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-peach/30 grid place-items-center">
            <UserX className="h-6 w-6 text-terracotta" strokeWidth={1.7} />
          </div>
          <p className="font-heading text-xl text-brand-primary mt-4">No matches for “{debouncedQ}”</p>
          <p className="text-sm text-brand-primary/60 mt-1">Try a different spelling, or search by email or phone.</p>
        </Card>
      )}

      {hasResults && (
        <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden" data-testid="people-results">
          <ul className="divide-y divide-brand-primary/10">
            {state.results.map((p) => (
              <PersonRow key={p.id ?? `${p.name}-${p.email}`} person={p} />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function PersonRow({ person }) {
  const tel = telHref(person.phone);
  return (
    <li
      className="p-5 flex items-center gap-4 hover:bg-cream/50 transition"
      data-testid={`people-result-${person.id || person.email || person.name}`}
    >
      <div className="h-11 w-11 rounded-full bg-cream grid place-items-center shrink-0 border border-brand-primary/10">
        <span className="font-heading text-sm text-terracotta">{initials(person.name)}</span>
      </div>

      <div className="min-w-0 flex-1 grid grid-cols-1 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,1fr)] gap-x-6 gap-y-1 items-center">
        {/* Name (primary) */}
        <div className="min-w-0 flex items-center gap-2 flex-wrap">
          <p className="font-medium text-brand-primary truncate text-[15px]">{person.name}</p>
          {person.membership_type && (
            <Badge className="rounded-full bg-sage/20 text-brand-primary border-0 text-[10px] uppercase tracking-wider">
              {person.membership_type}
            </Badge>
          )}
          {person.status && person.status !== person.membership_type && (
            <Badge className="rounded-full bg-peach/30 text-terracotta border-0 text-[10px] uppercase tracking-wider">
              {person.status}
            </Badge>
          )}
        </div>

        {/* Email (secondary) */}
        {person.email ? (
          <a
            href={`mailto:${person.email}`}
            className="text-sm text-brand-primary/70 hover:text-terracotta inline-flex items-center gap-1.5 min-w-0 truncate"
            data-testid={`people-email-${person.id}`}
            title={person.email}
          >
            <Mail className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{person.email}</span>
          </a>
        ) : (
          <span
            className="text-sm text-brand-primary/30 inline-flex items-center gap-1.5"
            aria-label="No email on file"
            title="No email on file"
          >
            <Mail className="h-3.5 w-3.5" /> —
          </span>
        )}

        {/* Phone (secondary) */}
        {tel ? (
          <a
            href={tel}
            className="text-sm text-brand-primary/70 hover:text-terracotta inline-flex items-center gap-1.5"
            data-testid={`people-phone-${person.id}`}
          >
            <Phone className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{person.phone}</span>
          </a>
        ) : (
          <span
            className="text-sm text-brand-primary/30 inline-flex items-center gap-1.5"
            aria-label="No phone on file"
            title="No phone on file"
          >
            <Phone className="h-3.5 w-3.5" /> —
          </span>
        )}
      </div>
    </li>
  );
}

function NotConnectedState({ isAdmin }) {
  return (
    <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-10 text-center" data-testid="people-not-connected">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-peach/40 grid place-items-center">
        <PlugZap className="h-7 w-7 text-terracotta" strokeWidth={1.6} />
      </div>
      <p className="font-heading text-2xl text-brand-primary mt-4">Pushpay ChMS not connected yet</p>
      <p className="text-sm text-brand-primary/65 mt-2 max-w-md mx-auto">
        People Lookup pulls live from your Church Community Builder database. Once it's connected,
        staff will be able to search members by name, email, or phone in under 5 seconds.
      </p>
      {isAdmin ? (
        <Link
          to="/settings"
          data-testid="people-go-to-settings"
          className="inline-flex items-center gap-2 mt-5 rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-6 py-2.5 text-sm font-medium shadow-warm transition"
        >
          Connect in Settings
        </Link>
      ) : (
        <p className="text-xs uppercase tracking-wider text-brand-primary/50 mt-5">
          Ask an admin to link Pushpay ChMS in Settings → Integrations.
        </p>
      )}
    </Card>
  );
}
